import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { firstValueFrom, Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { FileDetails, FormParams, PilotData, PilotDataFilesDto } from "src/app/models/models";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import {
	SuccessSnackbarComponent,
	SuccessSnackbarData,
} from "src/app/shared/components/cbta-snackbar/success-snackbar.component";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { defaultListDateRange } from "src/app/shared/utils/list-date-filter.utils";

@Component({
	selector: "app-files",
	standalone: false,
	templateUrl: "./files.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./files.component.scss",
})
export class PilotDataFilesComponent implements OnInit, OnDestroy {
	routeSub?: Subscription;
	queryParamsSub?: Subscription;
	filesSub?: Subscription;

	uploadForm!: UntypedFormGroup;
	public columns!: Columns[];
	public configuration!: Config;
	selectedFile?: FileDetails;

	@ViewChild("fileNameTpl", { static: true }) fileNameTpl!: TemplateRef<any>;
	@ViewChild("uploadedByTpl", { static: true }) uploadedByTpl!: TemplateRef<any>;
	@ViewChild("uploadedOnTpl", { static: true }) uploadedOnTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;

	DATE_FORMAT = CONSTANT.DATE_FORMAT;

	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	private filterStart = moment(this.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	private filterEnd = moment(this.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
	dateFilterEnabled = false;
	filters?: FormParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	pilot?: PilotData;
	files: FileDetails[] = [];
	selectedFiles: File[] = [];
	folder?: { id?: number; category: string; name?: string };
	allowedFileTypes: string = ".pdf";
	searchQuery: string = "";
	maxFileSize: number = 5 * 1024 * 1024; // 5MB
	isUploading: boolean = false;

	staffNumber?: string;
	folderId?: string;

	private searchTimeout: any;
	private snackBar = inject(MatSnackBar);

	sessionMasterData = this.sharedService.sessionMasterData;

	constructor(
		private fb: UntypedFormBuilder,
		private route: ActivatedRoute,
		private router: Router,
		public sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private cdr: ChangeDetectorRef,
	) {
		this.initUploadForm();
	}

	ngOnInit() {
		this.sharedService.setActivePage(RouteType.PILOT_DATA);
		this.initTableConfig();
		this.handleRouteParams();
		this.handleQueryParams();
	}

	ngOnDestroy() {
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
		if (this.queryParamsSub) {
			this.queryParamsSub.unsubscribe();
		}
		if (this.filesSub) {
			this.filesSub.unsubscribe();
		}
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	}

	initUploadForm(): void {
		this.uploadForm = this.fb.group({
			staffId: ["", [Validators.required]],
			name: ["", [Validators.required]],
			folderCategory: ["", [Validators.required]],
			files: [[], [Validators.required]],
		});
	}

	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			{
				key: "name",
				title: "File Name",
				cellTemplate: this.fileNameTpl,
				width: "35%",
			},
			{
				key: "uploadedBy",
				title: "Uploaded By",
				cellTemplate: this.uploadedByTpl,
				width: "25%",
			},
			{
				key: "lastUploadedDate",
				title: "Uploaded On",
				cellTemplate: this.uploadedOnTpl,
				width: "25%",
			},
			{
				key: "action",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "15%",
			},
		];
	}

	handleRouteParams() {
		this.routeSub = this.route.params.subscribe((params) => {
			this.staffNumber = params["staffId"];
			this.folderId = params["folderId"];
		});
	}

	handleQueryParams() {
		this.queryParamsSub = this.route.queryParams.subscribe((queryParams) => {
			if (queryParams["staffNumber"]) {
				this.staffNumber = queryParams["staffNumber"];

				this.pilot = {
					id: queryParams["staffNumber"],
					staffNumber: queryParams["staffNumber"],
					name: queryParams["name"] || "",
					lastUpdated: queryParams["lastUpdated"] || "",
				};

				const categoryCode = queryParams["category"] || "";
				const categoryName = queryParams["categoryName"] || "";
				const folderId = queryParams["folderId"] ? parseInt(queryParams["folderId"]) : undefined;

				this.folder = {
					id: folderId,
					category: categoryCode,
					name: categoryName,
				};

				if (!this.folderId) {
					this.folderId = queryParams["folderId"];
				}

				// Update form with the data
				this.uploadForm.patchValue({
					staffId: this.pilot.staffNumber,
					name: this.pilot.name,
					folderCategory: categoryCode,
				});

				this.loadDocuments();
			}
		});
	}

	loadDocuments() {
		if (!this.staffNumber || !this.folder?.category) {
			return;
		}

		this.loaderService.setLoading(true);

		const params = {
			staffNumber: this.staffNumber,
			category: this.folder.category,
			folderId: this.folderId,
			...(this.dateFilterEnabled ? { startDate: this.filterStart, endDate: this.filterEnd } : {}),
		};

		this.filesSub = this.httpService.getDocuments(params).subscribe({
			next: (result: { data: any }) => {
				const data = result.data;
				const serverFiles = data.data as FileDetails[];
				const paginationDetails = data.pagination;

				this.files = serverFiles;

				if (paginationDetails) {
					this.pagination = {
						...this.pagination,
						...paginationDetails,
					};
				}

				this.loaderService.setLoading(false);
			},
			error: (error: any) => {
				console.error("Error loading documents:", error);
				this.loaderService.setLoading(false);
			},
		});
	}

	onFileSelected(event: any): void {
		const fileList = event.target.files;

		if (!fileList || fileList.length === 0) return;

		const files = Array.from(fileList) as File[];
		const validFiles: File[] = [];

		for (const file of files) {
			if (!this.isValidFileType(file)) {
				this.showErrorSnackbar("Error! File upload has failed due to 'Invalid file type'. Please try again.");
				continue;
			}

			//handle by backend
			// if (!this.isValidFileSize(file)) {
			// 	this.showErrorSnackbar(
			// 		"Error! File upload has failed due to 'File size too large exceeding 5MB'. Please try again.",
			// 	);
			// 	continue;
			// }

			if (this.selectedFiles.some((existing) => existing.name === file.name)) {
				this.showErrorSnackbar(`File ${file.name} already selected.`);
				continue;
			}

			validFiles.push(file);
		}

		this.selectedFiles.push(...validFiles);
		this.updateFormFiles();

		event.target.value = "";
	}

	updateFormFiles(): void {
		this.uploadForm.get("files")?.setValue(this.selectedFiles);
		this.uploadForm.get("files")?.updateValueAndValidity();

		this.cdr.detectChanges();
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
			this.filterStart = moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
			this.filterEnd = moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
		} else {
			return;
		}

		this.loadDocuments();
	}

	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;
				this.filters = {
					...this.filters,
					page: value.page,
					size: value.limit,
				};
				this.loadDocuments();
				break;
			default:
				break;
		}
	}

	selectRow(row: FileDetails) {
		this.selectedFile = row;
	}

	deleteFile(row?: FileDetails) {
		const fileToDelete = row || this.selectedFile;

		if (!fileToDelete) {
			return;
		}

		const fileName = fileToDelete.name || "this file";

		if (confirm(`Are you sure you want to delete ${fileName}?`)) {
			this.loaderService.setLoading(true);

			this.httpService.deleteDocument(fileToDelete.id).subscribe({
				next: () => {
					this.files = (this.files || []).filter((file) => file.id !== fileToDelete.id);
					this.loaderService.setLoading(false);
					if (this.selectedFile?.id === fileToDelete.id) {
						this.selectedFile = undefined;
					}
				},
				error: (error) => {
					console.error("Error deleting document:", error);
					this.loaderService.setLoading(false);
				},
			});
		}
	}

	viewFileDetails(row?: FileDetails) {
		const fileToView = row || this.selectedFile;

		if (!fileToView) {
			console.error("No file selected for viewing details");
			return;
		}

		try {
			const link = document.createElement("a");
			link.href = fileToView.url;
			link.download = fileToView.name || "file";
			link.target = "_blank";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.error("Error viewing file:", error);
		}
	}

	extractFileName(fullPath: string): string {
		if (!fullPath) return "";

		const parts = fullPath.split(/[/\\]/);
		return parts[parts.length - 1] || fullPath;
	}

	uploadNewFile() {
		this.uploadForm.markAllAsTouched();
		this.uploadForm.updateValueAndValidity();

		this.uploadSelectedFiles();
	}

	async uploadSelectedFiles(): Promise<void> {
		if (!this.selectedFiles.length) return;

		this.isUploading = true;
		this.loaderService.setLoading(true);

		try {
			const formValue = this.uploadForm.value;

			const uploadPromises = this.selectedFiles.map((file) => {
				const dto: PilotDataFilesDto = {
					staffNumber: formValue.staffId,
					file: file,
					category: formValue.folderCategory,
					filename: file.name,
				};

				return firstValueFrom(this.httpService.pilotDataWithFiles(dto));
			});

			const results = await Promise.all(uploadPromises);
			this.onSuccessUpload(results);
		} catch (error) {
			this.onErrorUpload(error);
		}
	}

	getFolderDisplayName(categoryName: string): string {
		const folderCategories = this.sessionMasterData?.documentTypes || [];
		const categoryIndex = folderCategories.findIndex((type) => type.value === categoryName);
		const category = folderCategories[categoryIndex];

		if (category) {
			const folderNumber = (categoryIndex + 1).toString().padStart(2, "0");
			return `${folderNumber}. ${category.value}`;
		}

		return categoryName;
	}

	onSuccessUpload(results: any[]): void {
		this.isUploading = false;
		this.loaderService.setLoading(false);

		this.selectedFiles = [];
		this.updateFormFiles();

		// Show success notification
		this.snackBar.openFromComponent(SuccessSnackbarComponent, {
			data: {
				userName: this.pilot?.name || "",
				action: "upload",
				staffNumber: this.staffNumber || "",
				folderCategory: this.folder?.category || "",
			} as SuccessSnackbarData,
			duration: 4000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["success-snackbar"],
		});

		this.loadDocuments();
	}

	onErrorUpload(error: any): void {
		this.isUploading = false;
		this.loaderService.setLoading(false);

		let errorMessage = "Error! Failed to upload file. Please try again.";

		if (error?.error?.message) {
			errorMessage = error.error.message;
		}

		this.showErrorSnackbar(errorMessage);
	}

	isValidFileType(file: File): boolean {
		const validTypes = [
			"application/pdf",
			"application/x-pdf",
			"application/acrobat",
			"applications/vnd.pdf",
			"text/pdf",
			"text/x-pdf",
		];
		const fileName = file.name.toLowerCase();
		const hasValidExtension = fileName.endsWith(".pdf");

		return validTypes.includes(file.type) || hasValidExtension;
	}

	isValidFileSize(file: File): boolean {
		return file.size <= this.maxFileSize;
	}

	private showErrorSnackbar(message: string): void {
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
	}

	goBack() {
		if (!this.staffNumber) {
			this.router.navigate([RouteType.PILOT_DATA]);
			return;
		}

		this.router.navigate(["/pilot-data", this.staffNumber, "folders"], {
			queryParams: {
				staffNumber: this.staffNumber,
				name: this.pilot?.name,
			},
		});
	}

	formatDate(dateString: string): string {
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	clearAllFiles(): void {
		this.selectedFiles = [];
	}

	removeFile(index: number): void {
		if (Array.isArray(this.selectedFiles) && index >= 0 && index < this.selectedFiles.length) {
			this.selectedFiles.splice(index, 1);
		}
	}

	get staffId() {
		return this.uploadForm.get("staffId")!;
	}

	get name() {
		return this.uploadForm.get("name")!;
	}

	get folderCategory() {
		return this.uploadForm.get("folderCategory")!;
	}

	get formFiles() {
		return this.uploadForm.get("files")!;
	}

	get hasFiles(): boolean {
		return this.files ? this.files.length > 0 : false;
	}

	get selectedFilesDisplay(): string {
		return this.selectedFiles && this.selectedFiles.length > 0 ? this.selectedFiles.map((f) => f.name).join("\n") : "";
	}

	get folderDisplayName(): string {
		if (!this.folder) return "";

		if (this.folder.id) {
			const folderNumber = this.folder.id.toString().padStart(2, "0");
			return `${folderNumber}. ${this.folder.name || ""}`;
		}

		return this.folder.name || "";
	}

	get constant() {
		return CONSTANT;
	}
}
